<form [formGroup]="packForm">
  <!-- HEADER -->
  <header
    class="flex w-full flex-col border-b border-gray-200 bg-white px-4 pb-4"
  >
    <div class="flex flex-row items-center justify-between">
      <h1 class="text-2xl text-primary-500">
        {{ editMode ? "Edit Pack" : "Create Pack" }}
      </h1>

      <div class="flex items-center gap-8">
        <p-button
          label="Cancel"
          severity="secondary"
          (onClick)="backToPack()"
        />

        <p-button
          [label]="editMode ? 'Save Changes' : 'Create Pack'"
          icon="pi pi-check"
          (onClick)="submitForm()"
        />

        <app-connected-user-profile />
      </div>
    </div>

    <p class="text-sm">
      {{
        editMode
          ? "Update pack details and feature settings"
          : "Create a new pack and configure available features"
      }}
    </p>
  </header>

  <!-- MAIN -->
  <main class="p-4">
    <!-- GENERAL INFO -->
    <section class="mb-4 rounded-lg border border-gray-200 bg-white p-4">
      <h2 class="mb-4 text-lg text-primary-500">General Information</h2>

      <!-- Pack Name -->
      <div class="mb-4">
        <label class="text-sm text-primary-500" for="packName">Name</label>
        <input
          id="packName"
          type="text"
          pInputText
          formControlName="name"
          [fluid]="true"
        />
      </div>

      <!-- Description -->
      <div class="mb-4">
        <label class="text-sm text-primary-500" for="description"
          >Description</label
        >
        <textarea
          id="description"
          formControlName="description"
          pTextarea
          [autoResize]="true"
          class="w-full"
        ></textarea>
      </div>
    </section>

    <section class="mb-4 rounded-lg border border-gray-200 bg-white p-4">
      <h2 class="mb-4 text-lg text-primary-500">
        {{ useUniqueConfig ? "Pricing" : "Pricing by Company Size" }}
      </h2>

      @if (useUniqueConfig) {
        <div
          class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          [formGroup]="packForm.controls.uniqueSizeConfigForm"
        >
          <div>
            <label for="price" class="text-sm text-primary-500"
              >Price (EUR/year)</label
            >
            <p-inputNumber
              id="price"
              formControlName="price"
              [min]="0"
              mode="currency"
              currency="EUR"
              [fluid]="true"
            />
          </div>
          <div>
            <label for="userLimit" class="text-sm text-primary-500"
              >User Limit</label
            >
            <p-inputNumber
              id="userLimit"
              formControlName="userLimit"
              [min]="1"
              [fluid]="true"
            />
          </div>
          <div>
            <label for="tripLimit" class="text-sm text-primary-500"
              >Trip Limit</label
            >
            <p-inputNumber
              id="tripLimit"
              formControlName="tripLimit"
              [min]="1"
              [fluid]="true"
            />
          </div>
          <div>
            <label for="extraUserLimit" class="text-sm text-primary-500">
              Extra User Limit
              <span class="text-xs text-gray-400">(vide = illimité)</span>
            </label>
            <p-inputNumber
              id="extraUserLimit"
              formControlName="extraUserLimit"
              [min]="0"
              [fluid]="true"
            />
          </div>
          <div>
            <label for="bulkUpload" class="text-sm text-primary-500">
              Max files per bulk upload
              <span class="text-xs text-gray-400">(0 = désactivé)</span>
            </label>
            <p-inputNumber
              id="bulkUpload"
              formControlName="bulkUpload"
              [min]="0"
              [fluid]="true"
            />
          </div>
        </div>
      } @else {
        <p-tabs [value]="0">
          <p-tablist>
            @for (size of companySizes; track $index) {
              <p-tab [value]="$index"> {{ size }}</p-tab>
            }
          </p-tablist>
          @for (size of companySizes; track $index) {
            <p-tabpanel [value]="$index">
              <div
                class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
                [formGroup]="
                  packForm.controls.packSizeConfigForm.controls[size]
                "
              >
                <div>
                  <label for="price" class="text-sm text-primary-500"
                    >Price (EUR/year)</label
                  >
                  <p-inputNumber
                    id="price"
                    formControlName="price"
                    [min]="0"
                    mode="currency"
                    currency="EUR"
                    [fluid]="true"
                  />
                </div>

                <div>
                  <label for="userLimit" class="text-sm text-primary-500"
                    >User Limit</label
                  >
                  <p-inputNumber
                    id="userLimit"
                    formControlName="userLimit"
                    [min]="1"
                    [fluid]="true"
                  />
                </div>

                <div>
                  <label for="tripLimit" class="text-sm text-primary-500"
                    >Trip Limit</label
                  >
                  <p-inputNumber
                    id="tripLimit"
                    formControlName="tripLimit"
                    [min]="1"
                    [fluid]="true"
                  />
                </div>

                <div>
                  <label for="extraUserLimit" class="text-sm text-primary-500">
                    Extra User Limit
                    <span class="text-xs text-gray-400">(vide = illimité)</span>
                  </label>
                  <p-inputNumber
                    id="extraUserLimit"
                    formControlName="extraUserLimit"
                    [min]="0"
                    [fluid]="true"
                  />
                </div>

                <div>
                  <label for="bulkUpload" class="text-sm text-primary-500">
                    Max files per bulk upload
                    <span class="text-xs text-gray-400">(0 = désactivé)</span>
                  </label>
                  <p-inputNumber
                    id="bulkUpload"
                    formControlName="bulkUpload"
                    [min]="0"
                    [fluid]="true"
                  />
                </div>
              </div>
            </p-tabpanel>
          }
        </p-tabs>
      }
    </section>

    <!-- FEATURES -->
    <section class="mb-4 rounded-lg border border-gray-200 bg-white p-4">
      <h2 class="mb-4 text-lg text-primary-500">Features</h2>

      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <!-- Export PDF -->
        <div class="flex items-center gap-2">
          <p-checkbox
            formControlName="exportPDF"
            inputId="exportPDF"
            [binary]="true"
          />
          <label for="exportPDF" class="text-sm text-primary-500"
            >Export PDF</label
          >
        </div>

        <!-- Export Excel -->
        <div class="flex items-center gap-2">
          <p-checkbox
            formControlName="exportExcel"
            inputId="exportExcel"
            [binary]="true"
          />
          <label for="exportExcel" class="text-sm text-primary-500"
            >Export Excel</label
          >
        </div>

        <!-- Client Filter -->
        <div class="flex items-center gap-2">
          <p-checkbox
            formControlName="clientFilter"
            inputId="clientFilter"
            [binary]="true"
          />
          <label for="clientFilter" class="text-sm text-primary-500"
            >Client Filter</label
          >
        </div>
        <!-- Comparison Graph -->
        <div class="flex items-center gap-2">
          <p-checkbox
            formControlName="comparisonGraph"
            inputId="comparisonGraph"
            [binary]="true"
          />
          <label for="comparisonGraph" class="text-sm text-primary-500"
            >Comparison Graph</label
          >
        </div>

        <!-- Use API -->
        <div class="flex items-center gap-2">
          <p-checkbox
            formControlName="useApi"
            inputId="useApi"
            [binary]="true"
          />
          <label for="useApi" class="text-sm text-primary-500"
            >Use API (external /api/external/v1/*)</label
          >
        </div>

        <!-- Use Google API (requires Use API) -->
        <div class="flex items-center gap-2">
          <p-checkbox
            formControlName="useGoogleApi"
            inputId="useGoogleApi"
            [binary]="true"
          />
          <label
            for="useGoogleApi"
            class="text-sm"
            [class.text-primary-500]="!packForm.controls.useGoogleApi.disabled"
            [class.text-gray-400]="packForm.controls.useGoogleApi.disabled"
            >Use Google API</label
          >
        </div>

        <!-- API calls per month -->
        <div>
          <label for="apiCallsPerMonth" class="text-sm text-primary-500">
            API calls per month
            <span class="text-xs text-gray-400">(0 = unlimited)</span>
          </label>
          <p-inputNumber
            id="apiCallsPerMonth"
            formControlName="apiCallsPerMonth"
            [min]="0"
            [fluid]="true"
          />
        </div>
      </div>
    </section>

    <!-- DELETE pack -->
    @if (editMode) {
      <section class="mb-4 rounded-lg border border-gray-200 bg-white p-4">
        <h2 class="mb-4 text-lg text-primary-500">Delete pack</h2>
        <p-button
          severity="danger"
          icon="pi pi-trash"
          label="Delete Pack"
          (onClick)="confirmDelete()"
        ></p-button>
      </section>
    }
  </main>
</form>
